/**
 * Route suggestion that stays in navigable water.
 *
 * ⚠️ This is the feature the research warned about. Navionics has been reported
 * routing over land and through unsafe water, and that is the reason the
 * architecture doc says auto-routing must "validate against charted depth before
 * it is shown, not after". So:
 *
 *   1. The grid is built from the chart's own depth areas and land, using the
 *      SAME `isUnsafeDepth` predicate that drives the shading. A cell is
 *      navigable only if a charted depth area covers it and that depth clears
 *      the boat's safety depth.
 *   2. Cells with no charted depth are NOT navigable. Unknown is not passable.
 *   3. The result is re-checked with `checkRouteAgainstChart` before being
 *      offered, and presented as a suggestion the skipper accepts or rejects —
 *      never applied silently.
 *
 * It is a suggestion engine, not an autopilot.
 */

import type { Map as MapLibreMap } from 'maplibre-gl';

import { type BoatProfile, isUnsafeDepth } from '../core/boat';
import { type LatLon, greatCircleDistance } from '../core/geo';
import {
  type Cell,
  type Grid,
  createGrid,
  findPath,
  setNavigable,
  simplifyPath,
  straightenPath,
} from '../core/pathfind';
import { type ChartManifest, chartSourceId } from './style';
import { chartedDepthAt } from './chartDepth';

export interface AutoRouteOptions {
  /** Grid cell size in metres. Smaller finds narrower channels but costs more. */
  cellSizeM: number;
  /**
   * Extra clearance kept off unnavigable water, in cells. Higher stands the
   * route further off the shore.
   */
  shoreClearanceCells: number;
  /** Cap on A* expansions before giving up. */
  maxExpansions: number;
}

export const DEFAULT_AUTO_ROUTE_OPTIONS: AutoRouteOptions = {
  // 60 m resolves harbour channels without making the grid enormous: a 10 km
  // leg is about 170 cells a side.
  cellSizeM: 60,
  shoreClearanceCells: 2,
  maxExpansions: 250_000,
};

export type AutoRouteFailure =
  | 'no_chart_data'
  /** Chart exists but was not loaded for enough of the planning area. */
  | 'chart_not_loaded'
  | 'start_not_navigable'
  | 'end_not_navigable'
  | 'no_route_found'
  | 'search_exhausted'
  | 'area_too_large';

export interface AutoRouteResult {
  /** Suggested intermediate positions, excluding the given start and end. */
  waypoints: LatLon[];
  failure: AutoRouteFailure | null;
  /** Cells examined, for the "why did that fail" explanation. */
  gridCells: number;
  navigableCells: number;
  /**
   * Cells that had NO charted depth at all, as distinct from cells that were
   * charted but too shallow. A high number means the chart was not loaded for
   * the area, which is a completely different problem from shoal water and
   * needs a different message.
   */
  unchartedCells: number;
}

/** Cells beyond which the grid is refused rather than freezing the phone. */
const MAX_GRID_CELLS = 400_000;

export interface Bbox {
  south: number;
  west: number;
  north: number;
  east: number;
}

/**
 * The extent that must be ON SCREEN before a leg can be planned.
 *
 * `queryRenderedFeatures` only sees tiles rendered for the current viewport, so
 * any part of the planning grid outside the view reads as "no charted depth" and
 * therefore as unnavigable. Fitting the route alone is not enough: the grid is
 * padded well beyond it, so a large fraction of cells fall outside the view and
 * are wrongly treated as land — measured off Halifax as a grid twice the width
 * of the viewport, with only 19% of cells navigable.
 *
 * Worse, the endpoints sit at the edge of the route's own bounding box, so they
 * are exactly where the off-screen error lands, which is why a destination in
 * plenty of water is reported as unusable.
 *
 * The caller fits this box, waits for tiles, and only then builds the grid.
 */
export function planningViewportFor(
  from: LatLon,
  to: LatLon,
  options: AutoRouteOptions = DEFAULT_AUTO_ROUTE_OPTIONS,
): Bbox {
  return paddedBounds(from, to, options);
}

/** Padded planning box shared by the viewport helper and the grid builder. */
function paddedBounds(from: LatLon, to: LatLon, _options: AutoRouteOptions): Bbox {
  // Padding is a fraction of the LEG LENGTH applied to both axes, not of each
  // axis's own span. Per-axis padding gives a due-east leg zero latitude room,
  // so it cannot deviate around an obstruction and every such leg fails.
  const latSpan = Math.abs(to.lat - from.lat);
  const lonSpan = Math.abs(to.lon - from.lon);
  const midLat = (from.lat + to.lat) / 2;
  const lonScale = Math.max(0.05, Math.cos((midLat * Math.PI) / 180));

  const legSpanDeg = Math.hypot(latSpan, lonSpan * lonScale);
  const padDeg = legSpanDeg * 0.45 + 0.01;

  return {
    south: Math.min(from.lat, to.lat) - padDeg,
    north: Math.max(from.lat, to.lat) + padDeg,
    west: Math.min(from.lon, to.lon) - padDeg / lonScale,
    east: Math.max(from.lon, to.lon) + padDeg / lonScale,
  };
}

/**
 * Build a navigability grid over the bounding box of `from`..`to`.
 *
 * Reads back the depth areas MapLibre has loaded, so the route can only be
 * planned through water the app has actually seen. Anything not covered by a
 * loaded, sufficiently-deep depth area is unnavigable.
 */
export function buildNavigableGrid(
  map: MapLibreMap,
  manifest: ChartManifest,
  boat: BoatProfile,
  from: LatLon,
  to: LatLon,
  options: AutoRouteOptions,
): {
  grid: Grid;
  origin: LatLon;
  latStep: number;
  lonStep: number;
  unchartedCells: number;
} | null {
  const { south, north, west, east } = paddedBounds(from, to, options);
  const midLat = (south + north) / 2;
  const metresPerDegLat = 111_132;
  const metresPerDegLon = 111_320 * Math.cos((midLat * Math.PI) / 180);

  const latStep = options.cellSizeM / metresPerDegLat;
  const lonStep = options.cellSizeM / Math.max(1, metresPerDegLon);

  const width = Math.ceil((east - west) / lonStep);
  const height = Math.ceil((north - south) / latStep);
  if (width < 2 || height < 2) return null;
  if (width * height > MAX_GRID_CELLS) return null;

  const grid = createGrid(width, height);
  const depthLayers = [...manifest.sources]
    .sort((a, b) => b.band - a.band)
    .map((source) => `${chartSourceId(source)}-depth-area`)
    .filter((id) => map.getLayer(id));

  if (depthLayers.length === 0) return null;

  let unchartedCells = 0;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const lat = north - (y + 0.5) * latStep;
      const lon = west + (x + 0.5) * lonStep;
      const point = map.project([lon, lat]);
      if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) { unchartedCells += 1; continue; }

      const features = map.queryRenderedFeatures(point, { layers: depthLayers });
      if (features.length === 0) { unchartedCells += 1; continue; } // no charted depth

      // The same predicate AND the same band resolution the shading uses, so the
      // suggestion cannot route through water the chart is colouring as unsafe —
      // and, just as importantly, is not blocked out of water the chart is
      // colouring as deep. Taking the shallowest across bands did the latter:
      // one 0–18.3 m polygon on the general chart covers most of a harbour
      // approach and would close it to the planner entirely. See chartDepth.ts.
      const shallowest = chartedDepthAt(features)?.depthM ?? null;

      if (!isUnsafeDepth(shallowest, boat)) setNavigable(grid, x, y, true);
    }
  }

  return { grid, origin: { lat: north, lon: west }, latStep, lonStep, unchartedCells };
}

const cellToLatLon = (
  cell: Cell,
  origin: LatLon,
  latStep: number,
  lonStep: number,
): LatLon => ({
  lat: origin.lat - (cell.y + 0.5) * latStep,
  lon: origin.lon + (cell.x + 0.5) * lonStep,
});

const latLonToCell = (
  position: LatLon,
  origin: LatLon,
  latStep: number,
  lonStep: number,
): Cell => ({
  x: Math.floor((position.lon - origin.lon) / lonStep),
  y: Math.floor((origin.lat - position.lat) / latStep),
});

/**
 * Nearest navigable cell to `cell`, searched outward.
 *
 * A tapped waypoint often lands a few metres inside a shoal or on a pier. Nudging
 * it to the nearest navigable cell is more useful than refusing, but the limit is
 * tight: moving a waypoint far from where the skipper put it would be its own
 * kind of wrong.
 */
export function nearestNavigableCell(grid: Grid, cell: Cell, maxRadius = 8): Cell | null {
  for (let radius = 0; radius <= maxRadius; radius++) {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        // Only the ring at this radius.
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== radius) continue;
        const x = cell.x + dx;
        const y = cell.y + dy;
        if (x < 0 || y < 0 || x >= grid.width || y >= grid.height) continue;
        if (grid.navigable[y * grid.width + x] === 1) return { x, y };
      }
    }
  }
  return null;
}

/**
 * Suggest intermediate waypoints between two positions that stay in navigable
 * water.
 *
 * Returns the failure reason rather than a fallback route. There is no
 * circumstance in which a straight line is an acceptable substitute for a route
 * that could not be found.
 */
export function suggestRouteBetween(
  map: MapLibreMap,
  manifest: ChartManifest,
  boat: BoatProfile,
  from: LatLon,
  to: LatLon,
  options: AutoRouteOptions = DEFAULT_AUTO_ROUTE_OPTIONS,
): AutoRouteResult {
  const empty = { waypoints: [], gridCells: 0, navigableCells: 0, unchartedCells: 0 };

  const built = buildNavigableGrid(map, manifest, boat, from, to, options);
  if (!built) return { ...empty, failure: 'no_chart_data' };

  const { grid, origin, latStep, lonStep, unchartedCells } = built;
  const navigableCells = grid.navigable.reduce<number>((n, v) => n + v, 0);
  const stats = { gridCells: grid.width * grid.height, navigableCells, unchartedCells };

  if (navigableCells === 0) return { waypoints: [], failure: 'no_chart_data', ...stats };

  // Distinguish "your waypoint is on a shoal" from "the chart is not loaded
  // there". Blaming the skipper's waypoint when the real problem is missing
  // chart data sends them to move a perfectly good destination.
  const mostlyUncharted = unchartedCells > stats.gridCells * 0.5;

  const startCell = nearestNavigableCell(grid, latLonToCell(from, origin, latStep, lonStep));
  if (!startCell) {
    return { waypoints: [], failure: mostlyUncharted ? 'chart_not_loaded' : 'start_not_navigable', ...stats };
  }

  const goalCell = nearestNavigableCell(grid, latLonToCell(to, origin, latStep, lonStep));
  if (!goalCell) {
    return { waypoints: [], failure: mostlyUncharted ? 'chart_not_loaded' : 'end_not_navigable', ...stats };
  }

  const result = findPath(grid, startCell, goalCell, {
    clearancePenalty: options.shoreClearanceCells,
    maxExpansions: options.maxExpansions,
  });

  if (!result.path) {
    return {
      waypoints: [],
      failure: result.exhausted ? 'search_exhausted' : 'no_route_found',
      ...stats,
    };
  }

  // Collapse to the fewest legs that still stay clear.
  const simplified = straightenPath(grid, simplifyPath(result.path));

  // Drop the first and last: those are the skipper's own waypoints, and the
  // grid-snapped versions would move them.
  const intermediate = simplified.slice(1, -1)
    .map((cell) => cellToLatLon(cell, origin, latStep, lonStep));

  return { waypoints: intermediate, failure: null, ...stats };
}

/** Human-readable explanation of a failure, written for the skipper. */
export function describeAutoRouteFailure(failure: AutoRouteFailure): string {
  switch (failure) {
    case 'no_chart_data':
      return 'No charted depths are loaded here. Zoom to the area first so the chart is available.';
    case 'chart_not_loaded':
      return 'The chart is not loaded for enough of this area to plan through it. '
        + 'Zoom out so the whole leg and its surroundings are on screen, then try again.';
    case 'start_not_navigable':
      return 'The start is not in water your boat can use. Move it into charted, deep-enough water.';
    case 'end_not_navigable':
      return 'The destination is not in water your boat can use. Move it into charted, deep-enough water.';
    case 'no_route_found':
      return 'No route exists through charted water deep enough for your boat. There may be no navigable way through.';
    case 'search_exhausted':
      return 'The search gave up before finding a way. Try a shorter leg, or a coarser grid in settings.';
    case 'area_too_large':
      return 'That leg covers too much area to plan at this resolution. Try a coarser grid in settings.';
    default:
      return 'Could not suggest a route.';
  }
}

/** Straight-line distance, for reporting how much longer a suggestion is. */
export function routeLengthOf(points: readonly LatLon[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += greatCircleDistance(points[i - 1], points[i]);
  return total;
}
